import React, { useMemo } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../../../../components/ui/dialog';
import { Button } from '../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../components/ui/card';
import { cn } from '../../../../../../../lib/utils';
import type { MaterialData } from '../../../../../types/materials';

type HeatLotViewModalProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  materials: MaterialData[];
  heatLotItems?: Partial<PipeSummary>[];
  isLoading?: boolean;
  selectedPipe?: any;
  onOpenPipeReport?: (pipe: PipeSummary) => void;
};

type ClearanceStatus = 'PASS' | 'PENDING_REVIEW' | 'FAIL' | 'INCOMPLETE';

type PipeSummary = {
  id: string | number;
  pipeNo: string;
  heatNumber: string;
  lotId: string;
  sequence: number;
  clearance: ClearanceStatus;
};

const STATUS_LABELS: Record<ClearanceStatus, string> = {
  PASS: 'Pass',
  PENDING_REVIEW: 'Pending',
  FAIL: 'Fail',
  INCOMPLETE: 'Incomplete',
};

const statusChipClass: Record<ClearanceStatus, string> = {
  PASS: 'border-[#A7DCC2] bg-[#E8F8EF]',
  PENDING_REVIEW: 'border-[#F0D99A] bg-[#FFF3D8]',
  FAIL: 'border-[#F1B7B8] bg-[#FDE9E9]',
  INCOMPLETE: 'border-[#D8DDE5] bg-[#F2F4F7]',
};

const normalizeClearance = (value: unknown): ClearanceStatus => {
  const status = String(value || '').toUpperCase();
  if (!status) return 'INCOMPLETE';
  if (status === 'FAIL') return 'FAIL';
  if (status === 'PENDING_REVIEW' || status === 'PENDING') return 'PENDING_REVIEW';
  if (status === 'INCOMPLETE') return 'INCOMPLETE';
  return 'PASS';
};

const getPipeData = (material: MaterialData): any =>
  (material.details as any)?.pipeExtraDetails?.pipeData ||
  (material.details as any)?.pipe_extra_details?.pipeData ||
  (material.details as any)?.pipe_extra_details?.pipe_data ||
  {};

const toPipeSummary = (material: MaterialData): PipeSummary | null => {
  const details = material.details as any;
  const pipeData = getPipeData(material);
  const heatNumber = String(details?.pipeHeatNumber || details?.pipe_heat_number || '').trim();
  if (!heatNumber) return null;

  return {
    id: material.materialId ?? details?.pipePipeId ?? details?.pipeNo ?? `${heatNumber}-${pipeData?.sequence || 'pipe'}`,
    pipeNo: String(details?.pipeNo || details?.pipe_no || details?.pipePipeId || material.materialId),
    heatNumber,
    lotId: String(pipeData?.lotId || pipeData?.lot_id || 'LOT-UNASSIGNED'),
    sequence: Number(pipeData?.sequence || details?.pipeNo || material.materialId || 0),
    clearance: normalizeClearance(pipeData?.clearance),
  };
};

const percent = (count: number, total: number) => total ? Math.round((count / total) * 100) : 0;

const normalizeHeatLotItem = (item: Partial<PipeSummary>, index: number): PipeSummary => ({
  id: item.id ?? item.pipeNo ?? `heat-lot-pipe-${index}`,
  pipeNo: String(item.pipeNo || item.id || index + 1),
  heatNumber: String(item.heatNumber || '-'),
  lotId: String(item.lotId || 'LOT-UNASSIGNED'),
  sequence: Number(item.sequence || index + 1),
  clearance: normalizeClearance(item.clearance),
});

export const HeatLotViewModal: React.FC<HeatLotViewModalProps> = ({ isOpen, onOpenChange, materials, heatLotItems, isLoading = false, selectedPipe, onOpenPipeReport }) => {
  const pipeSummaries = useMemo(
    () => (
      heatLotItems
        ? heatLotItems.map(normalizeHeatLotItem)
        : materials.map(toPipeSummary).filter(Boolean) as PipeSummary[]
    ).sort((a, b) => (a.sequence || 0) - (b.sequence || 0)),
    [heatLotItems, materials],
  );

  const heatRows = useMemo(() => {
    const groups = new Map<string, PipeSummary[]>();
    pipeSummaries.forEach((pipe) => {
      const current = groups.get(pipe.heatNumber) || [];
      current.push(pipe);
      groups.set(pipe.heatNumber, current);
    });

    return Array.from(groups.entries()).map(([heatNumber, pipes]) => {
      const passCount = pipes.filter((pipe) => pipe.clearance === 'PASS').length;
      const lots = new Set(pipes.map((pipe) => pipe.lotId));
      return {
        heatNumber,
        pipes,
        lots: lots.size,
        passPercent: percent(passCount, pipes.length),
      };
    }).sort((a, b) => a.heatNumber.localeCompare(b.heatNumber));
  }, [pipeSummaries]);

  const lotRows = useMemo(() => {
    const groups = new Map<string, PipeSummary[]>();
    pipeSummaries.forEach((pipe) => {
      const key = `${pipe.heatNumber}__${pipe.lotId}`;
      const current = groups.get(key) || [];
      current.push(pipe);
      groups.set(key, current);
    });

    return Array.from(groups.values()).map((pipes) => {
      const passCount = pipes.filter((pipe) => pipe.clearance === 'PASS').length;
      return {
        heatNumber: pipes[0]?.heatNumber || '-',
        lotId: pipes[0]?.lotId || '-',
        count: pipes.length,
        passPercent: percent(passCount, pipes.length),
      };
    }).sort((a, b) => a.heatNumber.localeCompare(b.heatNumber) || a.lotId.localeCompare(b.lotId));
  }, [pipeSummaries]);

  const totals = useMemo(() => {
    const counts = pipeSummaries.reduce<Record<ClearanceStatus, number>>((acc, pipe) => {
      acc[pipe.clearance] += 1;
      return acc;
    }, { PASS: 0, PENDING_REVIEW: 0, FAIL: 0, INCOMPLETE: 0 });
    return {
      counts,
      completion: percent(counts.PASS, pipeSummaries.length),
      heats: heatRows.length,
      lots: new Set(pipeSummaries.map((pipe) => pipe.lotId)).size,
    };
  }, [heatRows.length, pipeSummaries]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="!left-0 !top-0 !h-screen !max-h-screen !w-screen !max-w-none !translate-x-0 !translate-y-0 m-0 gap-0 rounded-none border-0 bg-page-bg p-0 shadow-none [&>button]:hidden">
        <div className="flex h-screen flex-col overflow-hidden">
          <DialogHeader className="border-b border-[#E2E5EA] bg-white px-6 pb-3 pt-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex items-start gap-4">
                <Button onClick={() => onOpenChange(false)} variant="outline" className="h-9 w-9 shrink-0 rounded-lg border-[#E2E5EA] p-0">
                  <ArrowLeft className="h-5 w-5 text-text-grey" />
                </Button>
                <div>
                  <DialogTitle className="font-heading text-[20px] font-semibold text-blueprint-blue">
                    Heat & Lot View
                  </DialogTitle>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] text-heritage-grey">
                    <span>{selectedPipe?.displayName || selectedPipe?.name || 'Selected pipe'}</span>
                    <span>-</span>
                    <span>{pipeSummaries.length} pipes</span>
                    <span>-</span>
                    <span>{totals.heats} heats</span>
                    <span>-</span>
                    <span>{totals.lots} lots</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <div className="rounded-lg border border-[#E2E5EA] bg-page-bg px-4 py-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Overall Pass</div>
                  <div className="mt-1 font-mono text-[16px] font-semibold text-blueprint-blue">{totals.completion}%</div>
                </div>
                <div className="rounded-lg border border-[#E2E5EA] bg-page-bg px-4 py-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Exceptions</div>
                  <div className="mt-1 font-mono text-[16px] font-semibold text-blueprint-blue">
                    {totals.counts.PENDING_REVIEW + totals.counts.FAIL + totals.counts.INCOMPLETE}
                  </div>
                </div>
              </div>
            </div>
          </DialogHeader>

          <div className="grid min-h-0 flex-1 grid-cols-1">
            <div className="min-h-0 overflow-y-auto p-4 sm:p-6">
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
                <Card className="border-[#E2E5EA] bg-white">
              <CardContent className="p-4">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="font-heading text-[15px] font-semibold text-blueprint-blue">Heat x pipe completeness</h3>
                    <p className="mt-1 text-[12px] text-heritage-grey">Each square is one pipe, grouped by heat number.</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-[10px] text-heritage-grey">
                    {(Object.keys(STATUS_LABELS) as ClearanceStatus[]).map((status) => (
                      <div key={status} className="flex items-center gap-1">
                        <span className={cn('h-2.5 w-2.5 rounded-sm border', statusChipClass[status])} />
                        {STATUS_LABELS[status]}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  {isLoading && (
                    <div className="flex min-h-[340px] flex-col items-center justify-center rounded-lg border border-dashed border-[#D8DDE5] p-8 text-center">
                      <Loader2 className="h-9 w-9 animate-spin text-hero-red" />
                      <div className="mt-4 font-heading text-[16px] font-semibold text-blueprint-blue">Loading heat and lot data</div>
                      <div className="mt-2 text-[13px] text-heritage-grey">Fetching pipe mill heat and lot rows from the API.</div>
                    </div>
                  )}
                  {!isLoading && heatRows.map((row) => (
                    <div key={row.heatNumber} className="grid grid-cols-[86px_minmax(0,1fr)_170px] items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-page-bg">
                      <div>
                        <div className="font-mono text-[11px] font-semibold text-blueprint-blue">{row.heatNumber}</div>
                        <div className="text-[10px] text-heritage-grey">{row.pipes.length} pipes</div>
                      </div>
                      <div className="flex min-w-0 flex-wrap gap-1">
                        {row.pipes.map((pipe) => (
                          <button
                            key={pipe.id}
                            type="button"
                            onClick={() => onOpenPipeReport?.(pipe)}
                            title={`${pipe.pipeNo} · ${pipe.lotId} · ${STATUS_LABELS[pipe.clearance]}`}
                            className={cn(
                              'h-3.5 w-3.5 rounded-[3px] border transition',
                              statusChipClass[pipe.clearance],
                              onOpenPipeReport && 'cursor-pointer hover:scale-125 hover:ring-2 hover:ring-hero-red-20'
                            )}
                          />
                        ))}
                      </div>
                      <div className="grid grid-cols-[38px_minmax(0,1fr)_48px] items-center gap-2">
                        <div className="text-right font-mono text-[11px] text-text-grey">{row.passPercent}%</div>
                        <div className="h-1.5 rounded-full bg-[#EEF0F3]">
                          <div className="h-1.5 rounded-full bg-[#20865F]" style={{ width: `${row.passPercent}%` }} />
                        </div>
                        <div className="rounded-full bg-page-bg px-2 py-1 text-center text-[10px] text-text-grey">
                          Chem <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-galvanizing-green" />
                        </div>
                      </div>
                    </div>
                  ))}
                  {!isLoading && !heatRows.length && (
                    <div className="rounded-lg border border-dashed border-[#D8DDE5] p-8 text-center text-[13px] text-heritage-grey">
                      No pipe heat data available for this selection.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="space-y-4">
              <Card className="border-[#E2E5EA] bg-white">
                <CardContent className="p-4">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Summary</div>
                  <div className="mt-4 space-y-3">
                    <SummaryRow label="Overall pass" value={`${totals.completion}%`} />
                    <SummaryRow label="Heats" value={totals.heats} />
                    <SummaryRow label="Lots" value={totals.lots} />
                    <SummaryRow label="Pending" value={totals.counts.PENDING_REVIEW} />
                    <SummaryRow label="Fail" value={totals.counts.FAIL} />
                    <SummaryRow label="Incomplete" value={totals.counts.INCOMPLETE} />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-[#E2E5EA] bg-white">
                <CardContent className="p-4">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Lot Breakdown</div>
                  <div className="mt-3 max-h-[46vh] space-y-2 overflow-auto pr-1">
                    {lotRows.map((lot) => (
                      <div key={`${lot.heatNumber}-${lot.lotId}`} className="rounded-lg border border-[#EEF0F3] p-3">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <div className="font-mono text-[11px] font-semibold text-blueprint-blue">{lot.lotId}</div>
                            <div className="text-[10px] text-heritage-grey">{lot.heatNumber} · {lot.count} pipes</div>
                          </div>
                          <div className="font-mono text-[11px] font-semibold text-text-grey">{lot.passPercent}%</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const SummaryRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex items-center justify-between gap-3 border-b border-dashed border-[#E2E5EA] pb-2 last:border-b-0 last:pb-0">
    <span className="text-[12px] text-heritage-grey">{label}</span>
    <span className="font-mono text-[13px] font-semibold text-blueprint-blue">{value}</span>
  </div>
);
